// Guía de despacho INTERNA (decisión de Rodrigo 2026-10-23, «opción A»): mientras la guía 52 no se
// certifique (D-127), la guía es un documento interno impreso con folio propio (GDI), como en Random
// hoy. Se emite una vez sobre un despacho o un traslado confirmado; volver a pedirla la reimprime.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api/cliente';
import { useIntentos } from '../api/idempotencia';
import { PanelError } from '../comun/Estados';
import { EncabezadoImpreso, Imprimir } from '../comun/Imprimir';
import { Modal } from '../comun/Modal';
import { fecha, pesos } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';

export type OrigenGuia = { despachoId: string } | { trasladoId: string };

export const NOMBRE_TIPO_GUIA: Record<string, string> = {
  VENTA: 'Venta (se factura después)',
  FACTURA_PENDIENTE: 'Despacho de factura o boleta',
  TRASPASO: 'Traspaso entre bodegas',
  CONSIGNACION: 'Envío a consignación',
  RETORNO_CONSIGNACION: 'Retorno de consignación',
};

type Ubic = {
  codigo: string;
  nombre: string;
  tipo: string | null;
  cliente: { razonSocial: string; rut: string | null } | null;
};
type GuiaImpresa = {
  guiaId: string;
  numero: string;
  tipo: string;
  fecha: string;
  destino: string | null;
  patente: string | null;
  chofer: string | null;
  observaciones: string | null;
  motivo?: string;
  origen: Ubic;
  destinoUbicacion?: Ubic;
  cliente: {
    razonSocial: string;
    rut: string | null;
    giro: string | null;
    direccion: string | null;
  } | null;
  referencias: Array<{ tipo: string; numero: string }>;
  lineas: Array<{
    numero: number;
    sku: string;
    descripcion: string;
    unidad: string;
    cantidad: string;
    valor: string | null;
  }>;
  valorizada: boolean;
};

const claveDe = (o: OrigenGuia) => ('despachoId' in o ? o.despachoId : o.trasladoId);

/** Emite (pidiendo patente y chofer) o reimprime la guía interna de un despacho o traslado. */
export function GuiaInternaDe({
  origen,
  alTerminar,
}: {
  origen: OrigenGuia;
  alTerminar: () => void;
}) {
  const { empresa, puede } = useSesion();
  const empresaId = empresa!.id;
  const qc = useQueryClient();
  const intentos = useIntentos();
  const de = useQuery({
    queryKey: ['emp', empresaId, 'guia-interna-de', claveDe(origen)],
    queryFn: () =>
      api.get<{ guiaId: string; numero: string } | null>('/ventas/guias-internas/de', {
        query: origen,
      }),
  });
  const [patente, setPatente] = useState('');
  const [chofer, setChofer] = useState('');
  const [destino, setDestino] = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [guiaId, setGuiaId] = useState<string | null>(null);
  const emitir = useMutation({
    mutationFn: () => {
      const cuerpo = {
        ...origen,
        ...(patente.trim() ? { patente: patente.trim() } : {}),
        ...(chofer.trim() ? { chofer: chofer.trim() } : {}),
        ...(destino.trim() ? { destino: destino.trim() } : {}),
        ...(observaciones.trim() ? { observaciones: observaciones.trim() } : {}),
      };
      return api.post<{ guiaId: string }>('/ventas/guias-internas', cuerpo, {
        idempotencia: intentos.clave('guia-interna', cuerpo),
      });
    },
    onSuccess: async (r) => {
      intentos.cerrar();
      setGuiaId(r.guiaId);
      await qc.invalidateQueries({ queryKey: ['emp', empresaId, 'guias-internas'] });
      await qc.invalidateQueries({ queryKey: ['emp', empresaId, 'guia-interna-de'] });
    },
    onError: (e) => intentos.cerrar(e),
  });
  const imprimir = guiaId ?? de.data?.guiaId ?? null;
  if (imprimir) return <GuiaInternaImpresa guiaId={imprimir} alTerminar={alTerminar} />;
  if (de.isError) return <PanelError error={de.error} />;
  if (!de.isSuccess) return null;
  return (
    <Modal
      titulo="Guía de despacho interna"
      ancho="medio"
      alCerrar={alTerminar}
      pie={
        <>
          <button type="button" onClick={alTerminar}>
            Volver
          </button>
          <button
            type="button"
            className="primario"
            data-autofoco
            disabled={emitir.isPending || !puede('ventas.guia_interna.emitir')}
            onClick={() => emitir.mutate()}
          >
            {emitir.isPending ? 'Emitiendo…' : 'Emitir e imprimir'}
          </button>
        </>
      }
    >
      <p className="ayuda">
        Documento interno con folio propio (GDI), como en Random hoy. No es la guía electrónica 52:
        no va al SII.
      </p>
      <div className="fila-campos">
        <label className="campo">
          <span className="etiqueta">Patente</span>
          <input
            aria-label="Patente"
            maxLength={12}
            value={patente}
            onChange={(e) => setPatente(e.target.value.toUpperCase())}
          />
        </label>
        <label className="campo">
          <span className="etiqueta">Chofer</span>
          <input
            aria-label="Chofer"
            maxLength={80}
            value={chofer}
            onChange={(e) => setChofer(e.target.value)}
          />
        </label>
      </div>
      <label className="campo">
        <span className="etiqueta">Destino (si no es la dirección del cliente)</span>
        <input
          aria-label="Destino"
          maxLength={200}
          value={destino}
          onChange={(e) => setDestino(e.target.value)}
        />
      </label>
      <label className="campo">
        <span className="etiqueta">Observaciones</span>
        <input
          aria-label="Observaciones"
          maxLength={500}
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
        />
      </label>
      {!puede('ventas.guia_interna.emitir') && (
        <p className="apagado">Emitir la guía interna exige el permiso «Guía interna».</p>
      )}
      {!!emitir.error && <PanelError error={emitir.error} />}
    </Modal>
  );
}

/** La guía interna impresa. */
export function GuiaInternaImpresa({
  guiaId,
  alTerminar,
}: {
  guiaId: string;
  alTerminar: () => void;
}) {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const g = useQuery({
    queryKey: ['emp', empresaId, 'guias-internas', guiaId],
    queryFn: () => api.get<GuiaImpresa>(`/ventas/guias-internas/${guiaId}`),
  });
  if (g.isError) return <PanelError error={g.error} />;
  if (!g.data) return null;
  const x = g.data;
  const destinoTexto =
    x.destino ??
    (x.destinoUbicacion
      ? x.destinoUbicacion.cliente
        ? `${x.destinoUbicacion.nombre}`
        : `${x.destinoUbicacion.codigo} · ${x.destinoUbicacion.nombre}`
      : (x.cliente?.direccion ?? ''));
  const total = x.lineas.reduce((a, l) => a + Number(l.valor ?? 0), 0);
  return (
    <Imprimir alTerminar={alTerminar}>
      <article className="impreso guia-interna" data-guia-interna={x.numero}>
        <EncabezadoImpreso
          empresa={empresa!.razon_social}
          titulo={`Guía de despacho interna · ${NOMBRE_TIPO_GUIA[x.tipo] ?? x.tipo}`}
          numero={`GDI N° ${x.numero}`}
          detalle={
            <dl className="impreso-datos">
              <dt>Fecha</dt>
              <dd>{fecha(x.fecha)}</dd>
              {x.cliente && (
                <>
                  <dt>Cliente</dt>
                  <dd>
                    {x.cliente.razonSocial} {x.cliente.rut ?? ''}
                  </dd>
                </>
              )}
              {x.cliente?.giro && (
                <>
                  <dt>Giro</dt>
                  <dd>{x.cliente.giro}</dd>
                </>
              )}
              <dt>Desde</dt>
              <dd>
                {x.origen.codigo} · {x.origen.nombre}
              </dd>
              <dt>Destino</dt>
              <dd>{destinoTexto || '—'}</dd>
              <dt>Patente</dt>
              <dd>{x.patente ?? '—'}</dd>
              <dt>Chofer</dt>
              <dd>{x.chofer ?? '—'}</dd>
              <dt>Referencias</dt>
              <dd>{x.referencias.map((r) => `${r.tipo} ${r.numero}`).join(' · ')}</dd>
            </dl>
          }
        />
        <table className="impreso-tabla">
          <thead>
            <tr>
              <th>N°</th>
              <th>Código</th>
              <th>Descripción</th>
              <th>Unid.</th>
              <th className="der">Cantidad</th>
              {x.valorizada && <th className="der">Valor</th>}
            </tr>
          </thead>
          <tbody>
            {x.lineas.map((l) => (
              <tr key={l.numero}>
                <td>{l.numero}</td>
                <td>{l.sku}</td>
                <td>{l.descripcion}</td>
                <td>{l.unidad}</td>
                <td className="der">{Number(l.cantidad).toLocaleString('es-CL')}</td>
                {x.valorizada && <td className="der">{pesos(l.valor ?? '0')}</td>}
              </tr>
            ))}
          </tbody>
          {x.valorizada && (
            <tfoot>
              <tr>
                <td colSpan={5} className="der">
                  <b>Total</b>
                </td>
                <td className="der">
                  <b>{pesos(String(total))}</b>
                </td>
              </tr>
            </tfoot>
          )}
        </table>
        {(x.observaciones || x.motivo) && (
          <p>
            <b>Observaciones:</b> {x.observaciones ?? x.motivo}
          </p>
        )}
        <p className="impreso-nota">
          Documento interno, no tributario. No reemplaza la guía de despacho electrónica.
        </p>
        <p style={{ marginTop: '3rem' }}>
          Recibe conforme: ______________________ RUT: ____________ Fecha: ____/____/______
        </p>
      </article>
    </Imprimir>
  );
}
